/**
 * Mestre-detalhe: a linha escolhida na grade preenche o painel.
 *
 * ## Por que isto não é uma cópia do `master-detail-panel`
 *
 * A Fase 6 manda **mover e sanear, não reescrever** — e essa regra vale. Mas o
 * `master-detail-panel` (842 linhas) não é um widget maduro que muda de pasta:
 * ele tem **35 pontos de manipulação de DOM e uma única menção a `FormGroup`**.
 * Ele preenche campo procurando `<input>` com `querySelector`, escrevendo em
 * `.value`, disparando cinco eventos sintéticos por campo, e depois chamando
 * `removeFormErrorClasses` quatro vezes em `setTimeout` escalonado — porque o
 * Ant pintava o campo de vermelho, e ele brigava de volta forçando
 * `color: rgba(0,0,0,.85) !important`.
 *
 * Nada disso é lógica de mestre-detalhe. É tudo consequência de **não haver
 * modelo de valores** — o `dummyFormGroup` vazio do canvas antigo. O plano diz
 * isso em duas seções: com o modelo de valores, *"os controles não ficam
 * inválidos e não há o que apagar"*, e o caso do forçamento de cor é
 * *"deletar, não consertar"*.
 *
 * E há o fato que decide: aquele componente é usado **só** pelo
 * `canvas.component.html` do módulo antigo. Não está no caminho de render do
 * launcher, nem do `template-detail`, nem da grade copiada. Ele morre na Fase 8
 * junto com o canvas que o hospeda.
 *
 * O que sobra da funcionalidade é o que está aqui: pegar a linha escolhida e
 * escrever nos controles do painel. Com `FormGroup`, isso é escrever num mapa.
 */
import { ScreenNode } from '../model/node-props';
import { ScreenDocument } from '../model/screen-schema';

/** O que vai ser escrito: nome do controle → valor. */
export type PanelValues = Readonly<Record<string, unknown>>;

/**
 * Os valores que a linha entrega aos campos dos painéis nomeados.
 *
 * A resolução tenta, nesta ordem: o `fieldName` do campo, o **último segmento**
 * do `dataPath`, e o `dataPath` inteiro. A ordem não é arbitrária — a linha de
 * uma grade é um objeto plano com os nomes das colunas (`{ nome, preco }`),
 * enquanto o campo do painel costuma trazer o caminho completo dentro da tela
 * (`itens.preco`). Sem o último segmento, quase nada casaria.
 */
export function valuesForPanels(
  document: ScreenDocument,
  panelIds: readonly string[],
  row: unknown
): PanelValues {

  if (row === null || row === undefined || typeof row !== 'object') {
    return {};
  }

  const linha = row as Record<string, unknown>;
  const valores: Record<string, unknown> = {};

  for (const painel of findPanels(document.root, panelIds)) {
    collectInto(painel, linha, valores);
  }

  return valores;
}

/** Os painéis pedidos, onde quer que estejam na árvore. */
function findPanels(root: ScreenNode, panelIds: readonly string[]): readonly ScreenNode[] {
  if (panelIds.length === 0) {
    return [];
  }

  const procurados = new Set(panelIds);
  const achados: ScreenNode[] = [];

  visit(root, node => {
    if (procurados.has(node.id)) {
      achados.push(node);
    }
  });

  return achados;
}

/**
 * Desce o painel escrevendo o que a linha tem.
 *
 * Campo cuja chave não existe na linha **fica de fora**, em vez de receber
 * vazio. É a diferença entre "esta coluna não veio nesta linha" e "esta linha
 * apagou o campo": escrever vazio faria escolher uma linha limpar campos que a
 * grade nem conhece.
 */
function collectInto(
  node: ScreenNode,
  row: Record<string, unknown>,
  valores: Record<string, unknown>
): void {

  visit(node, atual => {
    const chave = resolveKey(atual, row);

    if (chave === null) {
      return;
    }

    valores[atual.id] = row[chave];
  });
}

/** Qual chave da linha corresponde a este campo, se alguma. */
function resolveKey(node: ScreenNode, row: Record<string, unknown>): string | null {
  const fieldName = node.binding?.fieldName;

  if (fieldName !== undefined && fieldName in row) {
    return fieldName;
  }

  const dataPath = node.binding?.dataPath;

  if (dataPath === undefined || dataPath.length === 0) {
    return null;
  }

  const ultimo = dataPath.split('.').pop() ?? '';

  if (ultimo.length > 0 && ultimo in row) {
    return ultimo;
  }

  return dataPath in row ? dataPath : null;
}

function visit(node: ScreenNode, fn: (node: ScreenNode) => void): void {
  fn(node);

  for (const filho of node.children ?? []) {
    visit(filho, fn);
  }
}
